import { groupBy, maxBy, sumBy } from 'lodash';
import { InvestmentHistory, Revaluation } from '../../types';

type QueryDependencies = {
  revaluations: Array<Revaluation>;
  investmentHistory: InvestmentHistory;
  newRevaluations: Array<Omit<Revaluation, 'revaluationId'>>;
  revaluationFormFields: Array<Partial<Revaluation>>;
};

function revaluationQueriesFactory({
  revaluations,
  investmentHistory,
  newRevaluations,
  revaluationFormFields,
}: QueryDependencies) {
  const getRevaluationsPerHoldingClass = (holdingClassId: string) => {
    return revaluations.filter((rev) => rev.holdingClassId === holdingClassId);
  };

  const getLatestRevaluationPerHoldingClass = (holdingClassId: string) => {
    return maxBy(
      revaluations.filter((rev) => rev.holdingClassId === holdingClassId),
      'ledgerDate',
    );
  };

  const getInvestmentsPerHoldingClass = (holdingClassId: string) => {
    return investmentHistory.filter(
      (investment) => investment?.holdingClassId === holdingClassId,
    );
  };

  const getNewRevaluationPerHoldingClass = (holdingClassId: string) => {
    return newRevaluations
      .filter((rev) => rev.holdingClassId === holdingClassId)
      .pop();
  };

  const findRevaluationFieldIndex = (
    holdingClassId: string,
    id: string,
    prefix?: 'revaluations' | 'newRevaluations',
  ) => {
    if (prefix === 'newRevaluations') {
      const index = newRevaluations.findIndex(
        (rev) => rev.holdingClassId === holdingClassId,
      );

      return {
        field: index !== -1 ? newRevaluations[index] : undefined,
        index: index !== -1 ? index : undefined,
      };
    }

    const index = revaluationFormFields.findIndex((field) =>
      field.holdingClassId === holdingClassId
        ? field.revaluationId === id
        : false,
    );

    return {
      field: index !== -1 ? revaluationFormFields[index] : undefined,
      index: index !== -1 ? index : undefined,
    };
  };

  const getRevaluationsGroupedByHoldingClass = () => {
    return groupBy(revaluations, 'holdingClassId');
  };

  const getUnitsPerHoldingClass = (
    investmentHistory: Partial<InvestmentHistory>,
    groupId: string,
  ) => {
    const holdingClassesInvestments = investmentHistory.filter(
      (investment) => investment?.holdingClassId === groupId,
    );
    return sumBy(holdingClassesInvestments, 'currentNumberOfUnits');
  };

  const getAllRevaluations = () => {
    return [
      ...revaluations.map((rev) => ({
        ...rev,
        isNew: false,
        currency: rev.currency,
        isRemoved: false,
      })),
      ...newRevaluations.map((rev) => ({
        ...rev,
        isNew: true,
        currency: rev.currency,
        isRemoved: rev.isRemoved ?? false,
      })),
    ];
  };

  return {
    getRevaluationsPerHoldingClass,
    getInvestmentsPerHoldingClass,
    getNewRevaluationPerHoldingClass,
    findRevaluationFieldIndex,
    getRevaluationsGroupedByHoldingClass,
    getUnitsPerHoldingClass,
    getAllRevaluations,
    getLatestRevaluationPerHoldingClass,
  };
}

export default revaluationQueriesFactory;
